<template>
  <div class="list-container">
    <div class="d-flex justify-content-between align-items-center">
      <h1 class="page-title">顧客毎の差引残高の一覧</h1>
      <div>
        <router-link :to="{ name: 'Home' }"> <!-- 業者情報詳細ページへのリンク -->
          <button type="button" class="btn btn-primary mx-1">ホーム</button>
        </router-link>
        <button @click="exportToExcel" class="btn btn-success">Excel出力</button>
      </div>
    </div>

    <!-- 検索バーの追加 -->
    <div class="search-container p-3 my-3" style="background-color: #f8f9fa;">
      <div class="row g-3 align-items-center">
        <div class="d-flex align-items-center mb-2 me-2 flex-wrap">
          <!-- 年月FromTo -->
          <div class="d-flex align-items-center my-2 me-3">
            <label class="col-form-label me-2">対象年月</label>

            <!-- From -->
            <div class="d-flex align-items-center me-2">
              <select class="form-select me-1" v-model="searchYearFrom" @change="syncPeriodToFrom" style="min-width: 120px">
                <option value="">年</option>
                <option v-for="year in years" :key="'from-' + year" :value="year">{{ year }}年</option>
              </select>

              <select class="form-select" v-model="searchMonthFrom" @change="syncPeriodToFrom" style="min-width: 100px">
                <option value="">月</option>
                <option v-for="month in months" :key="'from-month-' + month" :value="month">{{ month }}月</option>
              </select>
            </div>

            <label class="col-form-label me-2">〜</label>

            <!-- To -->
            <div class="d-flex align-items-center">
              <select class="form-select me-1" v-model="searchYearTo" style="min-width: 120px">
                <option value="">年</option>
                <option v-for="year in filteredYearsTo" :key="'to-' + year" :value="year">{{ year }}年</option>
              </select>

              <select class="form-select" v-model="searchMonthTo" style="min-width: 100px">
                <option value="">月</option>
                <option v-for="month in filteredMonthsTo" :key="'to-month-' + month" :value="month">{{ month }}月</option>
              </select>
            </div>
          </div>

          <!-- 顧客検索 -->
          <div class="d-flex align-items-center my-2 me-3">
            <div class="col-auto">
              <CustomerSelectBox
                v-model="searchCustomerName"
                :options="customers"
                placeholder="顧客名で検索"
                :formatter="c => c.customer_nm"
              />
            </div>
          </div>

          <div class="my-2">
            <button class="btn btn-primary mx-1" @click="searchCustomerMoneyInfos">検索</button>
            <button class="btn btn-secondary mx-1" @click="searchResetCustomerMoneyInfos">クリア</button>
          </div>
        </div>
      </div>
    </div>
    
    <div class="search-note alert alert-info py-2 my-2">
      <small>
        予定年月は From〜To の範囲検索ができます。<br>
        年月を未指定で顧客名のみ指定した場合は、その顧客の全期間（年月別）を表示します。
      </small>
    </div>

    <div class="table-wrapper">
      <table class="table table-bordered">
        <thead class="table-dark">
          <tr>
            <th class="text-nowrap">顧客名</th>
            <th class="text-nowrap">対象年月</th>
            <th class="text-nowrap">入金確認操作</th>
            <th class="text-nowrap">入金予定日</th>
            <th class="text-nowrap">入金予定金額(請求金額)</th>
            <th class="text-nowrap">入金日</th>
            <th class="text-nowrap">入金額</th>
            <th class="text-nowrap">振込手数料</th>
            <th class="text-nowrap">その他控除額</th>
            <th class="text-nowrap">消込合計</th>
            <th class="text-nowrap">未確認金額</th>
            <th class="text-nowrap">差引残高</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="customerMoneyInfo in customerMoneyInfos"
            :key="`${customerMoneyInfo.id}-${getRowYear(customerMoneyInfo)}-${getRowMonth(customerMoneyInfo)}`"
            :class="{ 'status-balance-zero': formatCurrency(customerMoneyInfo?.t_customer_balance?.balance) == '¥0' }"
          >
            <td class="text-nowrap">{{ customerMoneyInfo.customer_nm }}</td>
            <td class="text-nowrap">
              {{ formatYearMonth(customerMoneyInfo) }}
            </td>
            <td class="text-nowrap">
              <button
                class="btn btn-sm btn-success mx-1 my-1"
                @click="openCustomerPaymentConfirmModal(customerMoneyInfo, getRowYear(customerMoneyInfo), getRowMonth(customerMoneyInfo))"
              >
                入金確認登録
              </button>
            </td>
            <td class="text-nowrap">
              {{ calculateExpectedDepositDate(customerMoneyInfo.billing_timing_type, getRowYear(customerMoneyInfo), getRowMonth(customerMoneyInfo)) }}
            </td>
            <td class="text-nowrap">{{ formatCurrency(customerMoneyInfo?.customer_money_info?.expected_deposit_amount) }}</td>
            <td class="text-nowrap">{{ formatYearMonthDay(customerMoneyInfo?.customer_money_info?.payment_confirm_dt) }}</td>
            <td class="text-nowrap">{{ formatCurrency(customerMoneyInfo?.customer_money_info?.payment_confirm_amount) }}</td>
            <td class="text-nowrap">{{ formatCurrency(customerMoneyInfo?.customer_money_info?.transfer_fee ?? 0) }}</td>
            <td class="text-nowrap">{{ formatCurrency(customerMoneyInfo?.customer_money_info?.other_deduction_amount ?? 0) }}</td>
            <td class="text-nowrap">{{ formatCurrency(getSettledAmount(customerMoneyInfo?.customer_money_info)) }}</td>
            <td class="text-nowrap">{{ formatCurrency(customerMoneyInfo?.t_customer_balance?.unconfirm_amount) }}</td>
            <td class="text-nowrap">{{ formatCurrency(customerMoneyInfo?.t_customer_balance?.balance) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <!-- 入金確認モーダル -->
  <CustomerPaymentConfirmModal
    ref="customerPaymentConfirmModal"
    @onClose="fetchCustomerMoneyInfos"
    @onUpdated="fetchCustomerMoneyInfos"
  />
</template>

<script>
import { ref, watch, watchEffect, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useRoute } from 'vue-router';
import * as XLSX from 'xlsx'; // SheetJSをインポート
import {
  getCustomerMoneyConditions,
  getSelectBoxCustomers
} from '@/api/axios';
import CustomerSelectBox from '@/components/searchSelectBoxes/CustomerSelectBox.vue';
import CustomerPaymentConfirmModal from './CustomerPaymentConfirmModal.vue'; // 同じ階層のモーダルをインポート
import { getSettledAmount } from '@/utils/paymentAmounts';

export default {
  components: {
    CustomerSelectBox,
    CustomerPaymentConfirmModal,
  },
  setup() {
    const title = ref('顧客毎の差引残高');

    watchEffect(() => {
      document.title = title.value;
    });

    const router = useRouter();
    const route = useRoute();
    const customers = ref([]);
    const customerMoneyInfos = ref([]);
    const years = ref([]);
    const months = ref([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]);
    const searchYearFrom = ref('');
    const searchMonthFrom = ref('');
    const searchYearTo = ref('');
    const searchMonthTo = ref('');
    const searchCustomerName = ref('');
    const targetYear = ref('');
    const targetMonth = ref('');
    const customerPaymentConfirmModal = ref(null); // モーダル用のref
    
    // 現在の年月を取得
    const currentDate = new Date()
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;
    for (let i = currentYear - 5; i <= currentYear + 5; i++) {
      years.value.push(i);
    }

    const parseQueryNumber = (value) => {
      if (value === undefined || value === null || value === '') return '';
      return parseInt(value, 10);
    };

    const fetchCustomerMoneyInfos = async () => {
      if (searchYearFrom.value && searchMonthFrom.value) {
        targetYear.value = Number(searchYearFrom.value);
        targetMonth.value = Number(searchMonthFrom.value);
      } else {
        targetYear.value = currentYear;
        targetMonth.value = currentMonth;
      }

      try {
        const response = await getCustomerMoneyConditions({
          year_from: searchYearFrom.value,
          month_from: searchMonthFrom.value,
          year_to: searchYearTo.value,
          month_to: searchMonthTo.value,
          customer_id: searchCustomerName.value
        });
        customerMoneyInfos.value = response.data;
      } catch (error) {
        console.error('顧客毎の金額情報データの取得に失敗しました:', error);
      }

      // 顧客情報（検索用）
      try {
        const response = await getSelectBoxCustomers();
        customers.value = response.data;
      } catch (error) {
        console.error('顧客情報ーデータの取得に失敗しました:', error);
      }
    };

    const ymToNumber = (y, m) => {
      if (!y || !m) return null;
      return Number(y) * 100 + Number(m);
    };

    // URL復元では動かさず、ユーザーがFromを変更したときだけToを揃える。
    const syncPeriodToFrom = () => {
      if (!searchYearFrom.value || !searchMonthFrom.value) return;
      searchYearTo.value = searchYearFrom.value;
      searchMonthTo.value = searchMonthFrom.value;
    };

    const filteredYearsTo = computed(() => {
      if (!searchYearFrom.value) return years.value;
      return years.value.filter((y) => Number(y) >= Number(searchYearFrom.value));
    });

    const filteredMonthsTo = computed(() => {
      if (!searchYearFrom.value || !searchMonthFrom.value) return months.value;
      if (searchYearTo.value > searchYearFrom.value) return months.value;
      if (searchYearTo.value === searchYearFrom.value) {
        return months.value.filter((m) => m >= Number(searchMonthFrom.value));
      }
      return months.value;
    });

    watch([searchYearFrom, searchMonthFrom, searchYearTo, searchMonthTo], () => {
      const from = ymToNumber(searchYearFrom.value, searchMonthFrom.value);
      const to = ymToNumber(searchYearTo.value, searchMonthTo.value);
      if (from && to && from > to) {
        searchYearTo.value = searchYearFrom.value;
        searchMonthTo.value = searchMonthFrom.value;
      }
    });

    const buildSearchQuery = () => ({
      year_from: searchYearFrom.value,
      month_from: searchMonthFrom.value,
      year_to: searchYearTo.value,
      month_to: searchMonthTo.value,
      customer_id: searchCustomerName.value
    });

    const normalizeQueryValue = (value) => {
      if (value === undefined || value === null) return '';
      return String(value);
    };

    const isSameSearchQuery = (queryA, queryB) => {
      const keys = ['year_from', 'month_from', 'year_to', 'month_to', 'customer_id'];
      return keys.every((key) => normalizeQueryValue(queryA[key]) === normalizeQueryValue(queryB[key]));
    };

    const applyRouteQuery = (query) => {
      const hasYearFrom = Object.prototype.hasOwnProperty.call(query, 'year_from');
      const hasMonthFrom = Object.prototype.hasOwnProperty.call(query, 'month_from');
      const hasYearTo = Object.prototype.hasOwnProperty.call(query, 'year_to');
      const hasMonthTo = Object.prototype.hasOwnProperty.call(query, 'month_to');
      const hasLegacyYear = Object.prototype.hasOwnProperty.call(query, 'year');
      const hasLegacyMonth = Object.prototype.hasOwnProperty.call(query, 'month');

      const legacyYear = hasLegacyYear ? parseQueryNumber(query.year) : '';
      const legacyMonth = hasLegacyMonth ? parseQueryNumber(query.month) : '';

      const initialYearFrom = hasYearFrom ? parseQueryNumber(query.year_from) : (legacyYear || currentYear);
      const initialMonthFrom = hasMonthFrom ? parseQueryNumber(query.month_from) : (legacyMonth || currentMonth);
      const initialYearTo = hasYearTo ? parseQueryNumber(query.year_to) : (legacyYear || currentYear);
      const initialMonthTo = hasMonthTo ? parseQueryNumber(query.month_to) : (legacyMonth || currentMonth);

      searchYearFrom.value = initialYearFrom;
      searchMonthFrom.value = initialMonthFrom;
      searchYearTo.value = initialYearTo;
      searchMonthTo.value = initialMonthTo;
      searchCustomerName.value = query.customer_id || '';
    };

    // 入金予定日の表示調整
    const calculateExpectedDepositDate = (billingTimingType, year, month) => {
      if (!year || !month) return '';
      const baseDate = new Date(year, month - 1); // 検索年月の月初め（0-indexed）

      // デフォルトは翌月末
      let depositDate = new Date(baseDate.getFullYear(), baseDate.getMonth() + 1 + 1, 0); // 翌月末 = 翌々月の0日

      switch (billingTimingType) {
        case 0:
          depositDate = new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 15);
          break;
        case 1:
          depositDate = new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 25);
          break;
        case 2:
          depositDate = new Date(baseDate.getFullYear(), baseDate.getMonth() + 2, 0); // 翌月末
          break;
        case 3:
          depositDate = new Date(baseDate.getFullYear(), baseDate.getMonth() + 2, 5);
          break;
        case 4:
          depositDate = new Date(baseDate.getFullYear(), baseDate.getMonth() + 2, 10);
          break;
      }

      return `${depositDate.getFullYear()}年${depositDate.getMonth() + 1}月${depositDate.getDate()}日`;
    };

    const formatYearMonthDay = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;  // 日付部分だけを取得
    };

    const getRowYear = (customerMoneyInfo) => {
      return customerMoneyInfo?.customer_money_info?.target_year || targetYear.value;
    };

    const getRowMonth = (customerMoneyInfo) => {
      return customerMoneyInfo?.customer_money_info?.target_month || targetMonth.value;
    };

    const formatYearMonth = (customerMoneyInfo) => {
      const year = getRowYear(customerMoneyInfo);
      const month = getRowMonth(customerMoneyInfo);
      if (!year || !month) return '';
      return `${year}年${month}月`;
    };

    // Format currency
    const formatCurrency = (value) => {
      if (value) {
        return new Intl.NumberFormat('ja-JP', {
          style: 'currency',
          currency: 'JPY',
        }).format(value);
      }
      return '¥0';

    };

    const openCustomerPaymentConfirmModal = (customerMoneyInfo, year, month) => {
      if (customerPaymentConfirmModal.value) {
        customerPaymentConfirmModal.value.show({
          customerInfo: customerMoneyInfo,
          year,
          month
        });
      }
    };
    
    // 顧客金額情報の検索をするメソッド
    const searchCustomerMoneyInfos = async () => {
      const query = buildSearchQuery();
      if (isSameSearchQuery(route.query, query)) {
        await fetchCustomerMoneyInfos();
        return;
      }

      router.push({
        path: '/customer-money-conditions',
        query
      });
    };

    // 顧客金額情報の検索リセットをするメソッド
    const searchResetCustomerMoneyInfos = async () => {
      searchYearFrom.value = currentYear;
      searchMonthFrom.value = currentMonth;
      searchYearTo.value = currentYear;
      searchMonthTo.value = currentMonth;
      searchCustomerName.value = '';
      targetYear.value = currentYear;
      targetMonth.value = currentMonth;

      const query = buildSearchQuery();
      if (isSameSearchQuery(route.query, query)) {
        await fetchCustomerMoneyInfos();
        return;
      }

      router.replace({
        path: '/customer-money-conditions',
        query
      });
    };

    // クエリパラメータの監視（画面遷移後の自動反映）
    watch(
      () => route.query,
      (newQuery) => {
        applyRouteQuery(newQuery);
        fetchCustomerMoneyInfos();
      },
      { immediate: true }
    );

    // Excel出力機能
    const exportToExcel = () => {
      const now = new Date();
      const formattedDate = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;

      const data = customerMoneyInfos.value.map((moneyInfo) => ({
        顧客名: moneyInfo.customer_nm,
        対象年月: formatYearMonth(moneyInfo),
        入金予定日: calculateExpectedDepositDate(
          moneyInfo.billing_timing_type,
          getRowYear(moneyInfo),
          getRowMonth(moneyInfo)
        ),
        '入金予定金額(請求金額)': formatCurrency(
          moneyInfo?.customer_money_info?.expected_deposit_amount
        ),
        入金日: formatYearMonthDay(
          moneyInfo?.customer_money_info?.payment_confirm_dt
        ),
        入金額: formatCurrency(
          moneyInfo?.customer_money_info?.payment_confirm_amount
        ),
        振込手数料: formatCurrency(moneyInfo?.customer_money_info?.transfer_fee ?? 0),
        その他控除額: formatCurrency(moneyInfo?.customer_money_info?.other_deduction_amount ?? 0),
        消込合計: formatCurrency(getSettledAmount(moneyInfo?.customer_money_info)),
        未確認金額: formatCurrency(
          moneyInfo?.t_customer_balance?.unconfirm_amount
        ),
        差引残高: formatCurrency(
          moneyInfo?.t_customer_balance?.balance
        ),
      }));

      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, '顧客差引残高');

      const filename = `顧客差引残高_${formattedDate}.xlsx`;
      XLSX.writeFile(workbook, filename);
    };

    return {
      title,
      customers,
      customerMoneyInfos,
      years,
      months,
      targetYear,
      targetMonth,
      searchYearFrom,
      searchMonthFrom,
      searchYearTo,
      searchMonthTo,
      searchCustomerName,
      filteredYearsTo,
      filteredMonthsTo,
      syncPeriodToFrom,
      fetchCustomerMoneyInfos,
      calculateExpectedDepositDate,
      getRowYear,
      getRowMonth,
      formatYearMonth,
      customerPaymentConfirmModal,
      openCustomerPaymentConfirmModal,
      searchCustomerMoneyInfos,
      searchResetCustomerMoneyInfos,
      formatYearMonthDay,
      formatCurrency,
      getSettledAmount,
      exportToExcel, // メソッドを返す
    };
  }
};
</script>

<style scoped>
.building-item {
  border: 1px solid #ddd;
  padding: 10px;
  margin-bottom: 10px;
  border-radius: 5px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.building-item:hover {
  background-color: #f5f5f5;
}

/* テーブルラッパーのスタイル */
.table-wrapper {
  max-height: calc(100vh - 300px); /* テーブルの高さを指定して、縦スクロールを発生させる */
  overflow-y: auto;  /* 縦スクロールを許可 */
  background: #fff;
  border: 1px solid #BBBBBB;
}

.table-wrapper table{
  background: #FFFFFF;
  border: 1px solid #BBBBBB;
  margin-bottom: 0;
}

.table-wrapper thead th {
  position: sticky;
  top: 0;
  background: #003366 !important; /* 背景色を設定し、スクロール中に背景が透けるのを防ぐ */
  color: #fff !important;
  z-index: 10;
}

.table-wrapper .th-sticky{
  z-index: 100 !important;
}

/* 横スクロール時に固定するカラム */
.table-wrapper th.sticky-left, td.sticky-left {
  position: sticky;
  left: 0;
  min-width: 64px;
  width: 64px;
  max-width: 64px;
  background: #f0f8ff;
  box-shadow: inset -1px 0 #BBBBBB; /* 内側にボーダー風の影をつける */
  z-index: 50;
}

.table-wrapper th.sticky-left-second, td.sticky-left-second {
  position: sticky;
  left: 64px;
  min-width: 120px;
  width: 120px;
  max-width: 120px;
  background: #f0f8ff;
  box-shadow: inset -1px 0 #BBBBBB; /* 内側にボーダー風の影をつける */
  z-index: 49;
}

table th,
table td{
  font-size: 14px;
}

.btn-cell{
  width: 184px;
}

.status-no-billing td {
  background-color: #FFD5EC !important;
}

.status-no-sending td {
  background-color: #FFDBC9 !important;
}

.status-no-schedule td {
  background-color: #CCFFCC !important;
}

.status-no-confirm td {
  background-color: #FFFFCC !important;
}

.status-all-ok td {
  background-color: #E6FFE9 !important;
}

.status-balance-zero td {
  background-color: #defcde !important;
}
</style>
